import { History, Copy, Trash2 } from 'lucide-react';
import { CalculatorResult } from '../../../types/worker.types';
import { Card, CardContent, CardHeader, CardTitle, Button } from '../../ui';

interface CalculatorHistoryProps {
  results: CalculatorResult[];
  onClearHistory: () => void;
}

export function CalculatorHistory({ results, onClearHistory }: CalculatorHistoryProps) {
  const copyResult = async (result: CalculatorResult) => {
    try {
      await navigator.clipboard.writeText(`${result.result.toFixed(2)} ${result.unit}`);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
    }
  };

  const getToolLabel = (tool: string) => {
    switch (tool) {
      case 'pente': return 'Pente';
      case 'denivele': return 'Dénivelé';
      case 'ngf': return 'NGF';
      default: return tool;
    }
  };

  const formatInputs = (tool: string, inputs: Record<string, number>) => {
    switch (tool) {
      case 'pente':
        return `H: ${inputs.hauteur}m, D: ${inputs.distance}m`;
      case 'denivele':
        return `Alt1: ${inputs.altitude1}m, Alt2: ${inputs.altitude2}m`;
      case 'ngf':
        return `Rel: ${inputs.altitudeRelative}m, NGF: ${inputs.referenceNGF}m`;
      default:
        return Object.entries(inputs)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
    }
  };

  if (results.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5" />
            <span>Historique</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucun calcul dans l'historique</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5" />
            <span>Historique</span>
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
              {results.length}
            </span>
          </CardTitle>
          <Button
            onClick={onClearHistory}
            variant="outline"
            size="sm"
            icon={Trash2}
          >
            Effacer
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {results.map((result) => (
            <div
              key={result.id}
              className="p-3 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getToolLabel(result.tool)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(result.timestamp).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <Button
                  onClick={() => copyResult(result)}
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                />
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  {formatInputs(result.tool, result.inputs)}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {result.result.toFixed(2)} {result.unit}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}